import React from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import { CustomField } from '../CustomFieldBuilder';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';

/** Valeurs de rôle acceptées par la base (CHECK sur `volunteers.role`). */
export const ROLE_VALUES = ['judge', 'staff', 'volunteer'] as const;

export const CHAMP_ROLE_DEFAUT: CustomField = {
  id: 'role',
  label: 'Tu viens comme',
  type: 'select',
  required: true,
  scope: 'team',
  order: 0,
  options: ['Juge', 'Staff', 'Bénévole'],
  is_role: true,
  role_map: { Juge: 'judge', Staff: 'staff', Bénévole: 'volunteer' },
};

interface Props {
  field: CustomField;
  onChange: (field: CustomField) => void;
}

/**
 * Éditeur de LA question de rôle — structurelle, donc hors du constructeur de
 * champs générique : elle ne se supprime pas et chaque proposition porte une
 * valeur.
 *
 * L'organisateur écrit ses propres propositions (« Juge de couloir »,
 * « Montage du matériel ») et dit à quoi chacune correspond. Le libellé lui
 * appartient ; la valeur appartient au système, qui s'en sert pour bâtir le
 * planning des juges.
 */
const RoleQuestionEditor: React.FC<Props> = ({ field, onChange }) => {
  const { t } = useTranslation();
  const options = field.options || [];
  const roleMap = field.role_map || {};

  const setOption = (index: number, libelle: string) => {
    const ancien = options[index];
    const nextOptions = [...options];
    nextOptions[index] = libelle;
    // La correspondance suit le renommage, sinon la proposition perdrait sa
    // valeur au premier caractère tapé.
    const nextMap: Record<string, string> = {};
    Object.entries(roleMap).forEach(([k, v]) => {
      nextMap[k === ancien ? libelle : k] = v;
    });
    onChange({ ...field, options: nextOptions, role_map: nextMap });
  };

  const setRole = (libelle: string, role: string) => {
    onChange({ ...field, role_map: { ...roleMap, [libelle]: role } });
  };

  const addOption = () => {
    const libelle = '';
    onChange({
      ...field,
      options: [...options, libelle],
      role_map: { ...roleMap, [libelle]: 'volunteer' },
    });
  };

  const removeOption = (index: number) => {
    const libelle = options[index];
    const nextMap = { ...roleMap };
    delete nextMap[libelle];
    onChange({
      ...field,
      options: options.filter((_, i) => i !== index),
      role_map: nextMap,
    });
  };

  const incomplete = options.filter((o) => !o.trim() || !roleMap[o]);

  return (
    <div>
      <h3 className="text-lg font-semibold mb-1">{t('volunteers.roleQuestionTitle')}</h3>
      <p className="text-sm text-muted-foreground mb-2">{t('volunteers.roleQuestionHelp')}</p>
      <Card>
        <CardContent className="p-4 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="role-label">{t('volunteers.roleQuestionLabel')}</Label>
            <Input
              id="role-label"
              value={field.label}
              onChange={(e) => onChange({ ...field, label: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label>{t('volunteers.roleQuestionOptions')}</Label>
            {options.map((libelle, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input
                  value={libelle}
                  placeholder={t('volunteers.roleQuestionOptionPlaceholder')}
                  onChange={(e) => setOption(i, e.target.value)}
                  className="flex-1"
                />
                <span className="text-muted-foreground text-sm shrink-0">→</span>
                <Select
                  value={roleMap[libelle] || ''}
                  onValueChange={(v) => setRole(libelle, v)}
                >
                  <SelectTrigger className="w-40 shrink-0">
                    <SelectValue placeholder={t('volunteers.roleQuestionPickRole')} />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLE_VALUES.map((r) => (
                      <SelectItem key={r} value={r}>{t(`volunteers.role.${r}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant="ghost"
                  size="icon"
                  className="shrink-0"
                  disabled={options.length <= 1}
                  onClick={() => removeOption(i)}
                  aria-label={t('common.delete')}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={addOption}>
              <Plus className="h-4 w-4 mr-1" />
              {t('volunteers.roleQuestionAddOption')}
            </Button>
          </div>

          {incomplete.length > 0 && (
            <p className="text-xs text-warning">
              {t('volunteers.roleQuestionIncomplete')}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default RoleQuestionEditor;
